/**
 * @file VideoThumbnail.js
 * @description Thumbnail component for a video asset.
 */

import { useState, useEffect } from 'react';
import Spinner from './spinners/Spinner';
import styles from './VideoThumbnail.module.scss';

const MAX_WIDTH = 100;
const MAX_HEIGHT = 56;
const VIDEO_THUMBNAIL_PREVIEW_SECOND = 3;

export default function VideoThumbnail({
    videoUrl,
    width = MAX_WIDTH,
    height = MAX_HEIGHT,
    alt = 'Video thumbnail',
}) {
    const [thumbnail, setThumbnail] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const resolveSizeClassName = () => {
        if (width === 80 && height === 64) {
            return styles.size80x64;
        }
        if (width === 80 && height === 56) {
            return styles.size80x56;
        }
        return styles.size100x56;
    };

    const sizeClassName = resolveSizeClassName();

    useEffect(() => {
        if (!videoUrl) {
            setThumbnail(null);
            setIsLoading(false);
            setHasError(true);
            return;
        }

        let isMounted = true;
        setIsLoading(true);
        setHasError(false);
        setThumbnail(null);

        async function generateThumbnail() {
            try {
                const video = document.createElement('video');
                video.crossOrigin = 'anonymous';
                video.src = videoUrl;
                video.preload = 'metadata';

                await new Promise((resolve, reject) => {
                    video.onloadedmetadata = resolve;
                    video.onerror = reject;
                });
                await new Promise((resolve, reject) => {
                    video.onloadeddata = resolve;
                    video.onerror = reject;
                });
                await new Promise(r => setTimeout(r, 100));

                const seekTime = Math.min(
                    VIDEO_THUMBNAIL_PREVIEW_SECOND,
                    video.duration,
                );
                await new Promise((resolve, reject) => {
                    video.currentTime = seekTime;
                    video.onseeked = resolve;
                    video.onerror = reject;
                });
                await new Promise(r => setTimeout(r, 100));

                const { videoWidth, videoHeight } = video;
                if (!videoWidth || !videoHeight) {
                    if (isMounted) {
                        setHasError(true);
                        setIsLoading(false);
                    }
                    return;
                }

                const aspect = videoWidth / videoHeight;
                let canvasWidth = videoWidth;
                let canvasHeight = videoHeight;
                if (canvasWidth > MAX_WIDTH) {
                    canvasWidth = MAX_WIDTH;
                    canvasHeight = canvasWidth / aspect;
                }
                if (canvasHeight > MAX_HEIGHT) {
                    canvasHeight = MAX_HEIGHT;
                    canvasWidth = canvasHeight * aspect;
                }

                const canvas = document.createElement('canvas');
                canvas.width = Math.round(canvasWidth);
                canvas.height = Math.round(canvasHeight);
                canvas
                    .getContext('2d')
                    .drawImage(video, 0, 0, canvas.width, canvas.height);

                if (isMounted) {
                    setThumbnail(canvas.toDataURL('image/png'));
                    setIsLoading(false);
                }
            } catch (error) {
                console.error('Failed to generate thumbnail:', error);
                if (isMounted) {
                    setHasError(true);
                    setIsLoading(false);
                }
            }
        }

        const timer = setTimeout(generateThumbnail, 100);
        return () => {
            isMounted = false;
            clearTimeout(timer);
        };
    }, [videoUrl]);

    if (isLoading) {
        return (
            <div
                className={`${styles.thumbnailBase} ${styles.loadingState} ${sizeClassName}`}
            >
                <Spinner width={30} />
            </div>
        );
    }

    if (hasError || !thumbnail) {
        return (
            <div
                className={`${styles.thumbnailBase} ${styles.errorState} ${sizeClassName}`}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="48"
                    viewBox="0 0 186 168"
                    fill="none"
                >
                    <path
                        d="M107.386 86.7865L81.9312 101.554C79.77 102.808 77 101.291 77 98.7688V69.2338C77 66.7113 79.77 65.1948 81.9312 66.4486L107.386 81.2161C109.538 82.4648 109.538 85.5378 107.386 86.7865Z"
                        fill="#2B6BE4"
                        fillOpacity="0.4"
                    />
                </svg>
            </div>
        );
    }

    return (
        <div
            className={`${styles.thumbnailBase} ${styles.thumbnailImageWrap} ${sizeClassName}`}
        >
            <img src={thumbnail} alt={alt} className={styles.thumbnailImage} />
        </div>
    );
}
